/**
 * Audio manager.
 *
 * Sounds are synthesised with the Web Audio API rather than shipped as files: they are
 * original by construction, add nothing to the bundle, and work offline with no asset
 * loading. The context is created lazily and only resumed after a real user gesture, which
 * is what Safari and Chrome autoplay policies require.
 */

export type SoundName =
  'remove' | 'blocked' | 'heart-lost' | 'level-complete' | 'level-failed' | 'ui-tap';

interface ToneSpec {
  readonly frequency: number;
  readonly endFrequency?: number;
  readonly duration: number;
  readonly type: OscillatorType;
  readonly gain: number;
  readonly delay?: number;
}

const SOUNDS: Record<SoundName, readonly ToneSpec[]> = {
  remove: [{ frequency: 620, endFrequency: 940, duration: 0.11, type: 'triangle', gain: 0.22 }],
  blocked: [
    { frequency: 190, endFrequency: 130, duration: 0.16, type: 'square', gain: 0.13 },
    { frequency: 96, duration: 0.1, type: 'sine', gain: 0.16, delay: 0.02 },
  ],
  'heart-lost': [{ frequency: 330, endFrequency: 165, duration: 0.24, type: 'sine', gain: 0.2 }],
  'level-complete': [
    { frequency: 523, duration: 0.12, type: 'triangle', gain: 0.2 },
    { frequency: 659, duration: 0.12, type: 'triangle', gain: 0.2, delay: 0.1 },
    { frequency: 784, duration: 0.2, type: 'triangle', gain: 0.22, delay: 0.2 },
  ],
  'level-failed': [
    { frequency: 392, duration: 0.16, type: 'sine', gain: 0.18 },
    { frequency: 294, duration: 0.16, type: 'sine', gain: 0.18, delay: 0.14 },
    { frequency: 196, duration: 0.3, type: 'sine', gain: 0.2, delay: 0.28 },
  ],
  'ui-tap': [{ frequency: 440, duration: 0.05, type: 'sine', gain: 0.1 }],
};

type AudioContextConstructor = new () => AudioContext;

function getAudioContextConstructor(): AudioContextConstructor | null {
  const scope = globalThis as unknown as {
    AudioContext?: AudioContextConstructor;
    webkitAudioContext?: AudioContextConstructor;
  };
  return scope.AudioContext ?? scope.webkitAudioContext ?? null;
}

export class AudioManager {
  private context: AudioContext | null = null;
  private master: GainNode | null = null;
  private unlocked = false;
  private failed = false;

  private soundEnabled = true;
  private soundVolume = 0.7;

  configure(settings: { soundEnabled: boolean; soundVolume: number }): void {
    this.soundEnabled = settings.soundEnabled;
    this.soundVolume = settings.soundVolume;
  }

  /** Must be called from a user gesture handler; safe to call repeatedly. */
  unlock(): void {
    if (this.failed || this.unlocked) return;
    const Constructor = getAudioContextConstructor();
    if (Constructor === null) {
      this.failed = true;
      return;
    }
    try {
      if (this.context === null) {
        this.context = new Constructor();
        this.master = this.context.createGain();
        this.master.gain.value = 1;
        this.master.connect(this.context.destination);
      }
      void this.context.resume();
      this.unlocked = true;
    } catch {
      // Audio is an enhancement: a failure here must never affect gameplay.
      this.failed = true;
    }
  }

  play(name: SoundName): void {
    if (!this.soundEnabled || !this.unlocked || this.failed) return;
    const context = this.context;
    const master = this.master;
    if (context === null || master === null) return;
    if (context.state === 'suspended') void context.resume();

    const now = context.currentTime;
    for (const tone of SOUNDS[name]) {
      const oscillator = context.createOscillator();
      const gain = context.createGain();
      const startAt = now + (tone.delay ?? 0);
      const endAt = startAt + tone.duration;

      oscillator.type = tone.type;
      oscillator.frequency.setValueAtTime(tone.frequency, startAt);
      if (tone.endFrequency !== undefined) {
        oscillator.frequency.linearRampToValueAtTime(tone.endFrequency, endAt);
      }

      const peak = tone.gain * this.soundVolume;
      gain.gain.setValueAtTime(0.0001, startAt);
      gain.gain.exponentialRampToValueAtTime(Math.max(0.0001, peak), startAt + 0.012);
      gain.gain.exponentialRampToValueAtTime(0.0001, endAt);

      oscillator.connect(gain);
      gain.connect(master);
      oscillator.start(startAt);
      oscillator.stop(endAt + 0.02);
      oscillator.onended = () => {
        oscillator.disconnect();
        gain.disconnect();
      };
    }
  }

  /** Called when the document is hidden or shown. */
  setActive(active: boolean): void {
    if (this.context === null) return;
    if (active) {
      if (this.unlocked) void this.context.resume();
    } else {
      void this.context.suspend();
    }
  }

  dispose(): void {
    void this.context?.close();
    this.context = null;
    this.master = null;
    this.unlocked = false;
  }
}

export const audioManager = new AudioManager();
